import {Lexer, CstParser, createToken} from 'chevrotain';

export const LParen = createToken({name: 'LParen', pattern: '('});
export const RParen = createToken({name: 'RParen', pattern: ')'});
export const LCurly = createToken({name: 'LCurly', pattern: '{'});
export const RCurly = createToken({name: 'RCurly', pattern: '}'});
export const LSquare = createToken({name: 'LSquare', pattern: '['});
export const RSquare = createToken({name: 'RSquare', pattern: ']'});
export const Dot = createToken({name: 'Dot', pattern: '.'});
export const Comma = createToken({name: 'Comma', pattern: ','});
export const Colon = createToken({name: 'Colon', pattern: ':'});
export const Equals = createToken({name: 'Equals', pattern: '='});
export const Asterisk = createToken({name: 'Asterisk', pattern: '*'});
export const DoubleAsterisk = createToken({name: 'DoubleAsterisk', pattern: '**'});
export const Plus = createToken({name: 'Plus', pattern: '+'});
export const Minus = createToken({name: 'Minus', pattern: '-'});
export const Identifier = createToken({
  name: 'Identifier',
  pattern: /[a-zA-Z_]\w*/,
});
export const HexInteger = createToken({
  name: 'HexInteger',
  pattern: /0[xX][0-9a-fA-F]+/,
});
export const Float = createToken({
  name: 'Float',
  pattern: /([0-9]*\.[0-9]+)|([0-9]+\.)/,
});
export const DecimalInteger = createToken({
  name: 'DecimalInteger',
  pattern: /(0+|[1-9][0-9]*)/,
});
export const InfixOperator = createToken({
  name: 'InfixOperator',
  pattern: /(or|and|not\s+in|in|==|!=|>=|<=|<|>|\/\/?|%)/,
  longer_alt: Identifier,
});
export const IfOperator = createToken({
  name: 'If',
  pattern: 'if',
  longer_alt: Identifier,
});
export const ElseOperator = createToken({
  name: 'Else',
  pattern: 'else',
  longer_alt: Identifier,
});

export const Not = createToken({
  name: 'Not',
  pattern: 'not',
  longer_alt: Identifier
});
export const WhiteSpace = createToken({
  name: 'WhiteSpace',
  pattern: /\s+/,
  group: Lexer.SKIPPED
});
export const LineTerminator = createToken({
  name: 'LineTerminator',
  pattern: /\\?(\n\r|\r|\n)/,
  group: Lexer.SKIPPED
});
export const StringToken = createToken({
  name: 'String',
  pattern: /'.*?'|".*?"/,
  longer_alt: Identifier,
});
export const BooleanToken = createToken({
  name: 'Boolean',
  pattern: /(True|False)/,
  longer_alt: Identifier,
});
export const NoneToken = createToken({
  name: 'None',
  pattern: 'None',
  longer_alt: Identifier,
});

export const allTokens = [
  WhiteSpace,
  LineTerminator,
  LParen,
  RParen,
  LCurly,
  RCurly,
  LSquare,
  RSquare,
  Dot,
  Comma,
  Colon,
  HexInteger,
  Float,
  DecimalInteger,
  StringToken,
  BooleanToken,
  NoneToken,
  DoubleAsterisk,
  Asterisk,
  Plus,
  Minus,
  IfOperator,
  ElseOperator,
  InfixOperator,
  Not,
  Equals,
  Identifier,
];

export const CustomTelemetryExpressionLexer = new Lexer(allTokens);

let parserInstance = null;

export default class CustomTelemetryExpressionParser extends CstParser {
  static get instance() {
    if (!parserInstance) parserInstance = new this();
    return parserInstance;
  }

  static parse(text) {
    const lexResult = CustomTelemetryExpressionLexer.tokenize(text);
    const parser = this.instance;
    parser.input = lexResult.tokens;
    const cst = parser.expr();
    return {
      cst,
      lexErrors: lexResult.errors,
      parseErrors: parser.errors
    };
  }

  constructor() {
    super(allTokens);
    const $ = this;
    $.RULE('expr', () => {
      $.SUBRULE($.primaryExpr);
      $.MANY(() => $.SUBRULE($.primaryExpressionOperation));
    });
    $.RULE('primaryExpr', () => {
      $.OR([
        {ALT: () => $.SUBRULE($.literal)},
        {ALT: () => $.SUBRULE($.callExpr)},
        {ALT: () => $.SUBRULE($.identifier)},
        {ALT: () => $.SUBRULE($.dictionaryExpr)},
        {ALT: () => $.SUBRULE($.listExpr)},
        {ALT: () => $.SUBRULE($.bracketsExpr)},
        {ALT: () => $.SUBRULE($.unaryOpExpr)},
      ]);
    });
    $.RULE('primaryExpressionOperation', () => {
      $.OR([
        {ALT: () => $.SUBRULE($.methodExpr)},
        {ALT: () => $.SUBRULE($.attribute)},
        {ALT: () => $.SUBRULE($.binaryOpExpr)},
        {ALT: () => $.SUBRULE($.conditionalExpression)},
        {ALT: () => $.SUBRULE($.indexingOrSliceExpr)},
        {ALT: () => $.SUBRULE($.callChainExpr)},
      ]);
    });
    $.RULE('binaryOpExpr', () => {
      $.SUBRULE($.binaryOperator);
      $.SUBRULE($.expr);
    });
    $.RULE('literal', () => {
      $.OR([
        {ALT: () => $.SUBRULE($.stringLiteral)},
        {ALT: () => $.SUBRULE($.booleanLiteral)},
        {ALT: () => $.SUBRULE($.hexIntegerLiteral)},
        {ALT: () => $.SUBRULE($.floatLiteral)},
        {ALT: () => $.SUBRULE($.decimalIntegerLiteral)},
        {ALT: () => $.SUBRULE($.noneLiteral)},
      ]);
    });
    $.RULE('stringLiteral', () => {
      $.CONSUME(StringToken);
    });
    $.RULE('booleanLiteral', () => {
      $.CONSUME(BooleanToken);
    });
    $.RULE('hexIntegerLiteral', () => {
      $.CONSUME(HexInteger);
    });
    $.RULE('floatLiteral', () => {
      $.CONSUME(Float);
    });
    $.RULE('decimalIntegerLiteral', () => {
      $.CONSUME(DecimalInteger);
    });
    $.RULE('noneLiteral', () => {
      $.CONSUME(NoneToken);
    });
    $.RULE('conditionalExpression', () => {
      $.CONSUME(IfOperator, {LABEL: 'ifKeyword'});
      $.SUBRULE2($.expr, {LABEL: 'conditionExpression'});
      $.CONSUME(ElseOperator, {LABEL: 'elseKeyword'});
      $.SUBRULE3($.expr, {LABEL: 'falseExpression'});
    });
    $.RULE('bracketsExpr', () => {
      $.CONSUME(LParen);
      $.SUBRULE($.expr);
      $.CONSUME(RParen);
    });

    $.RULE('indexingOrSliceExpr', () => {
      $.CONSUME(LSquare);
      $.OPTION(() => $.SUBRULE($.expr, {LABEL: 'fromIndex'}));
      $.OPTION2(() => {
        $.CONSUME(Colon);
        $.OPTION3(() => $.SUBRULE2($.expr, {LABEL: 'toIndex'}));
      });
      $.CONSUME(RSquare);
    });
    $.RULE('binaryOperator', () => {
      $.OR([
        {ALT: () => $.CONSUME(InfixOperator)},
        {ALT: () => $.CONSUME(Plus)},
        {ALT: () => $.CONSUME(Minus)},
        {ALT: () => $.CONSUME(DoubleAsterisk)},
        {ALT: () => $.CONSUME(Asterisk)},
      ]);
    });
    $.RULE('unaryOpExpr', () => {
      $.OR([
        {ALT: () => $.CONSUME(Not)},
        {ALT: () => $.CONSUME(Plus)},
        {ALT: () => $.CONSUME(Minus)},
      ]);
      $.SUBRULE($.expr);
    });
    $.RULE('listExpr', () => {
      $.CONSUME(LSquare);
      $.OPTION(() => {
        $.SUBRULE($.listItem);
        $.MANY(() => {
          $.CONSUME(Comma);
          $.SUBRULE2($.listItem);
        });
        $.OPTION2(() => $.CONSUME2(Comma));
      });
      $.CONSUME(RSquare);
    });
    $.RULE('listItem', () => {
      $.SUBRULE($.expr);
    });
    $.RULE('dictionaryExpr', () => {
      $.CONSUME(LCurly);
      $.OPTION(() => {
        $.SUBRULE($.keyValuePair);
        $.MANY(() => {
          $.CONSUME(Comma);
          $.SUBRULE2($.keyValuePair);
        });
        $.OPTION2(() => $.CONSUME2(Comma));
      });
      $.CONSUME(RCurly);
    });
    $.RULE('keyValuePair', () => {
      $.SUBRULE($.expr, {LABEL: 'key'});
      $.CONSUME(Colon);
      $.SUBRULE2($.expr, {LABEL: 'value'});
    });
    $.RULE('callExpr', () => {
      $.CONSUME(Identifier, {LABEL: 'functionName'});
      $.SUBRULE($.arguments);
    });
    $.RULE('callChainExpr', () => {
      $.SUBRULE($.arguments);
    });
    $.RULE('methodExpr', () => {
      $.CONSUME(Dot);
      $.SUBRULE($.identifier, {LABEL: 'functionName'});
      $.SUBRULE($.arguments);
    });
    $.RULE('arguments', () => {
      $.CONSUME(LParen);
      $.OPTION(() => {
        $.SUBRULE($.expr);
        $.MANY(() => {
          $.CONSUME(Comma);
          $.SUBRULE2($.expr);
        });
        $.OPTION2(() => $.CONSUME2(Comma));
      });
      $.CONSUME(RParen);
    });
    $.RULE('attribute', () => {
      $.CONSUME(Dot);
      $.CONSUME(Identifier);
    });
    $.RULE('identifier', () => {
      $.CONSUME(Identifier);
    });

    $.performSelfAnalysis();
  }
}
